import formatMessage from 'format-message';
import { requestTranscription } from './api';

const blockIconURI = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI4IiBmaWxsPSIjMEZCRDhDIi8+PHBhdGggZD0iTTIwIDI0YzIuMjEgMCAzLjk5LTEuNzkgMy45OS00TDI0IDEyYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0djhjMCAyLjIxIDEuNzkgNCA0IDR6bTYuNi00YzAgNC0zLjM4IDYuOC03LjA3IDYuOFMxMy40IDI0IDEzLjQgMjBIMTFjMCA0LjU1IDMuNjMgOC4zMSA4IDguOTVWMzNoMnYtNC4wNWM0LjM3LS42MyA4LTQuNCA4LTguOTVoLTIuNHoiIGZpbGw9IiNGRkYiLz48L3N2Zz4=';
const menuIconURI = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTIgMTRjMS42NiAwIDIuOTktMS4zNCAyLjk5LTNMMTUgNWMwLTEuNjYtMS4zNC0zLTMtM1M5IDMuMzQgOSA1djZjMCAxLjY2IDEuMzQgMyAzIDN6bTUuMy0zYzAgMy0yLjU0IDUuMS01LjMgNS4xUzYuNyAxNCA2LjcgMTFINWMwIDMuNDEgMi43MiA2LjIzIDYgNi43MlYyMWgydi0zLjI4YzMuMjgtLjQ4IDYtMy4zIDYtNi43MmgtMS43eiIgZmlsbD0iIzBGQkQ4QyIvPjxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiIGZpbGw9Im5vbmUiLz48L3N2Zz4=';

class STT2Extension {
    constructor (runtime) {
        this.runtime = runtime;
        this._speechResult = '';
        this._isListening = false;
        
        // Clean up on stop-all (red stop button).
        this.runtime.on('PROJECT_STOP_ALL', () => {
            this._isListening = false;
            this._speechResult = '';
        });
    }

    getInfo () {
        return {
            id: 'stt2',
            name: formatMessage({
                id: 'gui.extension.stt2.name',
                default: 'Speech Recognition 2.0'
            }),
            menuIconURI: menuIconURI,
            blockIconURI: blockIconURI,
            color1: '#D65C5C',
            color2: '#B94A4A',
            color3: '#B94A4A',
            blocks: [
                {
                    opcode: 'speech2Text',
                    blockType: 'command',
                    text: formatMessage({id: 'stt2.blocks.recognizeSpeech', default: 'recognize speech for [DURATION]s in [LANGUAGE]'}),
                    arguments: {
                        DURATION: {
                            type: 'number',
                            menu: 'countDown',
                            defaultValue: 3
                        },
                        LANGUAGE: {
                            type: 'string',
                            menu: 'languageMenu',
                            defaultValue: 'en'
                        }
                    }
                },
                {
                    opcode: 'getSpeechResult',
                    blockType: 'reporter',
                    text: formatMessage({id: 'stt2.blocks.speechResult', default: 'speech recognition result'})
                },
                {
                    opcode: 'clearSpeechResult',
                    blockType: 'command',
                    text: formatMessage({id: 'stt2.blocks.clearSpeechResult', default: 'clear speech recognition result'})
                }
            ],
            menus: {
                countDown: {
                    acceptReporters: true,
                    items: ['1', '2', '3', '4', '5', '8', '10']
                },
                languageMenu: {
                    acceptReporters: true,
                    items: [
                        {text: 'English', value: 'en'},
                        {text: 'Hindi', value: 'hi'},
                        {text: 'Bengali', value: 'bn'},
                        {text: 'Tamil', value: 'ta'},
                        {text: 'Telugu', value: 'te'},
                        {text: 'Marathi', value: 'mr'},
                        {text: 'Gujarati', value: 'gu'},
                        {text: 'Kannada', value: 'kn'},
                        {text: 'Malayalam', value: 'ml'},
                        {text: 'Punjabi', value: 'pa'},
                        {text: 'Odia', value: 'or'},
                        {text: 'Assamese', value: 'as'}
                    ]
                }
            }
        };
    }

    _blobToBase64 (blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                const base64Data = result.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    async speech2Text (args) {
        if (this._isListening) return;

        const durationMs = (Number(args.DURATION) || 3) * 1000;
        const locale = String(args.LANGUAGE) || 'en';

        try {
            this._isListening = true;
            this._speechResult = '';
            
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Choose a supported mime type
            let mimeType = 'audio/webm';
            if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                mimeType = 'audio/webm;codecs=opus';
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                mimeType = 'audio/mp4';
            }

            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            const audioChunks = [];

            mediaRecorder.ondataavailable = event => {
                if (event.data.size > 0) {
                    audioChunks.push(event.data);
                }
            };

            const recordPromise = new Promise(resolve => {
                mediaRecorder.onstop = resolve;
            });

            mediaRecorder.start();

            // Stop recording after duration
            setTimeout(() => {
                if (mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                }
            }, durationMs);

            await recordPromise;
            
            // Stop all tracks to release microphone
            stream.getTracks().forEach(track => track.stop());

            const audioBlob = new Blob(audioChunks, { type: mimeType });
            const audioBase64 = await this._blobToBase64(audioBlob);

            const response = await requestTranscription({
                audioBase64,
                mimeType,
                locale
            });

            if (response && response.transcription) {
                this._speechResult = response.transcription;
            } else {
                this._speechResult = '';
            }
            
        } catch (error) {
            console.error('[STT-2] Transcription error:', error);
            this._speechResult = 'Error: ' + error.message;
        } finally {
            this._isListening = false;
        }
    }

    getSpeechResult () {
        return this._speechResult;
    }

    clearSpeechResult () {
        this._speechResult = '';
    }
}

export default STT2Extension;
